import type { ComponentProps } from "react";
import { Icon as IconifyIcon } from "@iconify-icon/react";

import { cn } from "../../utils/cn";

type IconProps = Omit<ComponentProps<typeof IconifyIcon>, "icon" | "className" | "width" | "height"> & {
  icon: string;
  title?: string;
  "aria-label"?: string;
  className?: string;
  /** Explicit size in pixels - overrides className h-X/w-X */
  size?: number;
};

// Map Tailwind size classes to pixel values
const sizeMap: Record<string, number> = {
  "h-2": 8, "w-2": 8,
  "h-2.5": 10, "w-2.5": 10,
  "h-3": 12, "w-3": 12,
  "h-3.5": 14, "w-3.5": 14,
  "h-4": 16, "w-4": 16,
  "h-4.5": 18, "w-4.5": 18,
  "h-5": 20, "w-5": 20,
  "h-6": 24, "w-6": 24,
  "h-7": 28, "w-7": 28,
  "h-8": 32, "w-8": 32,
  "h-9": 36, "w-9": 36,
  "h-10": 40, "w-10": 40,
};

/** Extract pixel size from Tailwind className like "h-6 w-6" */
const extractSize = (className?: string): number | undefined => {
  if (!className) return undefined;
  const classes = className.split(/\s+/);
  for (const cls of classes) {
    if (sizeMap[cls]) return sizeMap[cls];
  }
  return undefined;
};

export const Icon = ({ icon, className, title, "aria-label": ariaLabel, size, ...rest }: IconProps) => {
  // Filter out any accidental 'classname' (lowercase) prop from third-party sources
  const { classname: _classname, ...safeProps } = rest as typeof rest & { classname?: string };

  // Determine size: explicit prop > className extraction > default 16px
  const pixelSize = size ?? extractSize(className) ?? 16;

  // iconify-icon web component needs explicit width/height props for reliable sizing
  // Also needs vertical-align: middle to properly center in flex containers.
  // `noobserver` disables the component's IntersectionObserver so the glyph
  // paints on mount instead of only when scrolled into the viewport. Below-fold
  // icons (e.g. status badges deep in the System Dashboard) otherwise rendered
  // as an empty slot until the user scrolled to them.
  return (
    <IconifyIcon
      icon={icon}
      title={title}
      width={pixelSize}
      height={pixelSize}
      noobserver
      className={cn("shrink-0", className)}
      style={{ verticalAlign: "middle" }}
      aria-label={ariaLabel ?? title}
      {...safeProps}
    />
  );
};
